import { useRef, useState } from 'react';
import { useWorkspaceStore } from '@/store/workspaceStore';
import { storeImageFile, MediaValidationError, ACCEPTED_WATERMARK_TYPES } from '@/lib/media';
import { SectionCard } from '@/components/SectionCard';
import { Slider } from '@/components/Slider';
import { Toggle } from '@/components/Toggle';
import { Button } from '@/components/Button';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import type { ReelProject, WatermarkPositionPreset, WatermarkSettings } from '@/types';

const POSITION_PRESETS: { value: WatermarkPositionPreset; label: string }[] = [
  { value: 'top-left', label: '↖ Top left' },
  { value: 'top-center', label: '↑ Top centre' },
  { value: 'top-right', label: '↗ Top right' },
  { value: 'center', label: '• Centre' },
  { value: 'bottom-left', label: '↙ Bottom left' },
  { value: 'bottom-center', label: '↓ Bottom centre' },
  { value: 'bottom-right', label: '↘ Bottom right' },
  { value: 'custom', label: '✥ Custom (drag)' },
];

export function WatermarkPanel({ project }: { project: ReelProject }) {
  const updateWatermark = useWorkspaceStore((s) => s.updateWatermark);
  const shared = useWorkspaceStore((s) => s.workspace.shared);
  const w = project.watermark.overridesShared ? project.watermark : shared.watermark;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const customUrl = useObjectUrl(w.source === 'custom' ? w.mediaId : null);

  function patch(fields: Partial<WatermarkSettings>) {
    updateWatermark(project.id, { ...fields, overridesShared: true });
  }

  async function handleFile(file: File) {
    setError(null);
    try {
      const asset = await storeImageFile(file);
      patch({ source: 'custom', mediaId: asset.id });
    } catch (err) {
      setError(err instanceof MediaValidationError ? err.message : 'Could not process that watermark file.');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2 text-xs dark:bg-slate-800">
        <span className="text-slate-500 dark:text-slate-400">Watermark comes from shared defaults unless overridden here.</span>
        <label className="flex shrink-0 items-center gap-2">
          <input
            type="checkbox"
            checked={project.watermark.overridesShared}
            onChange={(e) => updateWatermark(project.id, { overridesShared: e.target.checked })}
          />
          Override for this Reel
        </label>
      </div>

      <SectionCard title="Watermark">
        <Toggle label="Show watermark" checked={w.enabled} onChange={(v) => patch({ enabled: v })} />
        {w.enabled && (
          <>
            <div className="flex gap-2">
              <button
                onClick={() => patch({ source: 'default' })}
                className={`flex-1 rounded-lg border p-2 text-xs font-medium ${
                  w.source === 'default' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                <img src="/assets/watermark-default.svg" alt="Default watermark" className="mx-auto mb-1 h-8" />
                Default (repo)
              </button>
              <button
                onClick={() => (w.mediaId ? patch({ source: 'custom' }) : fileInputRef.current?.click())}
                className={`flex-1 rounded-lg border p-2 text-xs font-medium ${
                  w.source === 'custom' ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                {customUrl ? <img src={customUrl} alt="Custom watermark" className="mx-auto mb-1 h-8" /> : <span className="mb-1 block text-lg">⬆</span>}
                Custom upload
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_WATERMARK_TYPES.join(',')}
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
            </div>
            {w.source === 'custom' && (
              <Button size="sm" variant="ghost" onClick={() => fileInputRef.current?.click()}>
                Upload a different watermark
              </Button>
            )}
            {error && <p className="text-xs text-red-600">{error}</p>}

            <Slider label="Size" value={w.widthPercent} min={5} max={50} onChange={(v) => patch({ widthPercent: v })} formatValue={(v) => `${v}%`} />
            <Slider label="Opacity" value={w.opacity} min={0} max={1} step={0.05} onChange={(v) => patch({ opacity: v })} formatValue={(v) => `${Math.round(v * 100)}%`} />

            <div className="grid grid-cols-2 gap-2">
              {POSITION_PRESETS.map((p) => (
                <button
                  key={p.value}
                  onClick={() => patch({ positionPreset: p.value })}
                  className={`rounded-lg border px-2 py-1.5 text-xs ${
                    w.positionPreset === p.value ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30' : 'border-slate-200 dark:border-slate-700'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {w.positionPreset === 'custom' ? (
              <p className="text-xs text-slate-400">Drag the watermark directly in the preview player to position it.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Slider label="Horizontal offset" value={w.offsetXPercent} min={-20} max={20} onChange={(v) => patch({ offsetXPercent: v })} formatValue={(v) => `${v}%`} />
                <Slider label="Vertical offset" value={w.offsetYPercent} min={-20} max={20} onChange={(v) => patch({ offsetYPercent: v })} formatValue={(v) => `${v}%`} />
              </div>
            )}

            <Toggle label="Show throughout entire video" checked={w.alwaysVisible} onChange={(v) => patch({ alwaysVisible: v })} />
            {!w.alwaysVisible && (
              <div className="grid grid-cols-2 gap-2">
                <Slider label="Start at (s)" value={w.startAt} min={0} max={60} step={0.5} onChange={(v) => patch({ startAt: v })} />
                <Slider label="End at (s)" value={w.endAt ?? 10} min={0} max={60} step={0.5} onChange={(v) => patch({ endAt: v })} />
              </div>
            )}
            <Button size="sm" variant="danger" onClick={() => patch({ enabled: false })}>
              Remove watermark
            </Button>
          </>
        )}
      </SectionCard>
    </div>
  );
}
